import { useState } from "react";
import { Gift, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import ConfettiCelebration from "@/components/ConfettiCelebration";

const RedeemCodeSection = () => {
  const [code, setCode] = useState("");
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const queryClient = useQueryClient();

  const handleRedeem = async () => {
    const trimmed = code.trim();
    if (!trimmed) {
      toast.error("Please enter a code");
      return;
    }

    setIsRedeeming(true);
    try {
      const { error } = await supabase.rpc("redeem_promo_code", {
        input_code: trimmed,
      });

      if (error) throw error;

      setShowConfetti(true);
      toast.success("Success! 5 Forensic Credits added to your shield.");
      setCode("");
      queryClient.invalidateQueries({ queryKey: ["shield-credits"] });
      setTimeout(() => setShowConfetti(false), 4000);
    } catch (err: any) {
      const msg = err?.message || "Failed to redeem code";
      toast.error(msg);
    } finally {
      setIsRedeeming(false);
    }
  };

  return (
    <>
      <ConfettiCelebration trigger={showConfetti} />
      <div className="glass-card p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-lg bg-cyan/10">
            <Gift className="w-5 h-5 text-cyan" />
          </div>
          <h2 className="font-semibold text-lg">Redeem Code</h2>
        </div>

        <div className="p-4 rounded-xl bg-charcoal-medium space-y-3">
          <p className="text-sm text-muted-foreground">
            Have a Creator Code? Enter it below to claim your free credits.
          </p>
          <div className="flex gap-2">
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleRedeem()}
              placeholder="e.g. TEST123"
              className={cn(
                "flex-1 h-10 rounded-lg border border-glass-border bg-background px-3 text-sm",
                "placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-cyan/40"
              )}
              disabled={isRedeeming}
            />
            <button
              onClick={handleRedeem}
              disabled={isRedeeming || !code.trim()}
              className={cn(
                "px-5 h-10 rounded-lg font-semibold text-sm whitespace-nowrap",
                "bg-gradient-to-r from-cyan to-cyan-glow text-primary-foreground",
                "hover:scale-[1.02] transition-transform disabled:opacity-50 disabled:pointer-events-none"
              )}
            >
              {isRedeeming ? <Loader2 className="w-4 h-4 animate-spin" /> : "Redeem"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default RedeemCodeSection;
